import {O, RunInAction, RunInAction_Set} from "web-vcore";
import {MapTool, Tool, ToolData, ToolInterceptSubresult} from "./@Tool.js";
import {observable} from "mobx";
import {GetMapState} from "../../../../../Store/main/maps/$mapState.js";
import {GetOpenMapID} from "../../../../../Store/main/maps.js";
import {GetMapObject} from "../../../../../Server/DB/mapObjects.js";
import {store} from "../../../../../Store/index.js";
import {ResolveObjPosInWorld} from "../../../../../Server/DB/mapObjects/$mapObject.js";
import {MapObject} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {Rect, Vec2, Vec2Simple, Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {PolygonGuide} from "../PolygonGuide.js";
import React, {ReactNode} from "react";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {UpdateMapObject} from "../../../../../Server/Commands/MapObjectCommands.js";
import {Clone, GetEntries} from "js-vextensions";
import {CheckBox, Select, Spinner, Text} from "react-vcomponents";
import {GridCellsToPolygon, PolygonToGridCells} from "../../../../../Utils/ThreeJS/GridCellsUtils.js";
import {GetPolygonBounds} from "../../../../../Utils/ThreeJS/PolygonUtils.js";
import {mapZIndexes} from "../../MapSurface/MapZIndexes.js";
import {GetAsync, observer_mgl} from "mobx-graphlink";
import {action} from "mobx";
import {GuideShape, ObjMesh} from "../../../../../Server/DB/mapObjects/@MapObject/ObjMesh.js";
import {ignore} from "mobx-sync";

export enum MeshModifyBrushShape {
	square = "square",
	circle = "circle",
}
export enum MeshModifySubtool {
	grow = "grow",
	shrink = "shrink",
}

export class ToolState_MeshModify {
	@O accessor shape = MeshModifyBrushShape.circle;
	@O accessor size = 1;
	@O accessor subtool = MeshModifySubtool.grow;
	@action ChangeSubtool(subtool: MeshModifySubtool) {
		this.subtool = subtool;
	}
	ToggleSubtool() {
		this.ChangeSubtool(this.subtool == MeshModifySubtool.grow ? MeshModifySubtool.shrink : MeshModifySubtool.grow);
	}
	//@O alpha = 0.1;

	@O @ignore accessor opInProgress = false;
	@O @ignore accessor pendingObjMeshUpdates = observable.map<string, ObjMeshUpdate>();
	@action StartOp() {
		if (this.opInProgress) return;
		this.opInProgress = true; // this allows updating of the visual-preview
	}
	@action EndCurrentOp(clearPendingUpdates = true) {
		if (this.opInProgress) {
			this.opInProgress = false; // this blocks updating of the visual-preview
		}
		// have pending-update clearing occur regardless of operation-active status (since some tools show preview even prior to mouse-down/op-start)
		if (clearPendingUpdates) this.pendingObjMeshUpdates.clear(); // this clears the visual preview
	}
}

class ObjMeshUpdate {
	constructor(data: PartialBy<ObjMeshUpdate, "sendStarted">) { Object.assign(this, data); 	}
	meshIndex!: number;
	newPoints!: Vec2Simple[];
	sendStarted = false;
}

export class Tool_MeshModify extends Tool {
	toolType = MapTool.meshModify;
	get ToolState() { return store.main.maps.tools.toolStates.meshModify; }

	RenderInToolbox = observer_mgl(({data}: {data: ToolData.RenderInToolbox})=>{
		const state = this.ToolState;
		return (
			<>
				<Select ml={5} displayType="button bar" childStyle={{padding: "3px 12px"}} options={GetEntries(MeshModifySubtool, "ui")} value={state.subtool} onChange={val=>state.ChangeSubtool(val)}/>
				<Text ml={5}>Shape:</Text>
				<Select ml={5} displayType="button bar" childStyle={{padding: "3px 12px"}} options={GetEntries(MeshModifyBrushShape, "ui")} value={state.shape} onChange={val=>RunInAction_Set(()=>state.shape = val)}/>
				<Text ml={5}>Size:</Text>
				<Spinner ml={5} min={0} step={.5} value={state.size} onChange={val=>RunInAction_Set(()=>state.size = val)}/>
				{/*<Text ml={5}>Alpha:</Text>
				<Spinner ml={5} min={0} step={.1} value={toolState.alpha} onChange={val=>RunInAction_Set(()=>toolState.alpha = val)}/>*/}
			</>
		);
	});
	RenderInScene = observer_mgl_r3(({data}: {data: ToolData.RenderInScene})=>{
		const {map, mapState} = data;
		if (mapState.mousePos_world_0ms == null) return null;
		return <>
			<PolygonGuide {...{map, mapState}} forTool="meshModify"/>
			<mesh position={mapState.mousePos_world_0ms.NewZ(mapZIndexes.toolPreview).To3JPos()}>
				{this.ToolState.shape == MeshModifyBrushShape.square && <boxGeometry args={[this.ToolState.size, this.ToolState.size, 0.1]}/>}
				{this.ToolState.shape == MeshModifyBrushShape.circle && <circleGeometry args={[this.ToolState.size / 2, 24]}/>}
				<meshBasicMaterial color="rgb(255,255,255)" opacity={0.5} transparent/>
			</mesh>
		</>;
	});

	Tool_OnDeactivate(data: ToolData.Tool_OnDeactivate): void {
		this.ToolState.EndCurrentOp();
	}

	shiftDown_subtoolOnRelease?: MeshModifySubtool|n;
	Document_OnKeyDown(data: ToolData.Document_OnKeyDown): ToolInterceptSubresult | void {
		// if shift-key is pressed, toggle grow/shrink
		if (data.event.key == "Shift" && this.shiftDown_subtoolOnRelease == null) {
			const oldSubtool = this.ToolState.subtool;
			RunInAction_Set(()=>this.ToolState.ToggleSubtool());
			this.shiftDown_subtoolOnRelease = oldSubtool;
			return {absorb: true};
		}

		// if escape-key is pressed, cancel the current mouse-drag operation, and clear any pending mesh updates
		if (data.event.key == "Escape") {
			this.ToolState.EndCurrentOp();
			return {absorb: true};
		}
	}
	Document_OnKeyUp(data: ToolData.Document_OnKeyDown): ToolInterceptSubresult | void {
		// if shift-key is released (and shift-key being pressed caused a toggle already), toggle grow/shrink back to original
		if (data.event.key == "Shift" && this.shiftDown_subtoolOnRelease) {
			this.ToolState.ChangeSubtool(this.shiftDown_subtoolOnRelease);
			this.shiftDown_subtoolOnRelease = null;
			return {absorb: true};
		}
	}

	Scene_OnPointerDown(data: ToolData.Scene_OnPointerDown) {
		if (data.event.button != 0) return; // left mouse-button only
		this.ToolState.StartOp();
	}
	//Scene_OnPointerMove = this.Override("Scene_OnPointerMove", data=>{
	Scene_OnPointerMove(data: ToolData.Scene_OnPointerMove) {
		if (!this.ToolState.opInProgress) return;

		const mapID = GetOpenMapID.CatchBail(null);
		const mapState = GetMapState.CatchBail(null, mapID);
		if (mapID == null || mapState == null) return;
		const obj = GetMapObject.CatchBail(null, mapState.activeObject, mapID);
		if (obj == null) return;
		const objPos = ResolveObjPosInWorld.CatchBail(null, obj);
		if (objPos == null) return;

		const objMesh = ObjMesh.GetSelected(obj);
		if (objMesh == null) return;
		const state = this.ToolState;

		// The object's world-position is also the grid's origin. (ie. the bottom-left corner of its [0,0] cell)
		// So to calculate what grid-pos the mouse is over, we need to get the mouse's world-position, and then subtract the object's world-position from it.
		const guideSize = objMesh.guide_shape == GuideShape.square && objMesh.guide_size ? objMesh.guide_size : 1;
		/*const guideSize_x = objMesh.guide_shape == GuideShape.square && objMesh.guide_size ? objMesh.guide_size.x : 1;
		const guideSize_y = objMesh.guide_shape == GuideShape.square && objMesh.guide_size ? objMesh.guide_size.y : 1;*/

		//const gridPosMouseIsOver = data.mousePos_world.Minus(objPos.ToVec3()).NewX(x=>(x / guideSize_x).FloorTo(1)).NewY(y=>(y / guideSize_y).FloorTo(1));
		// factor in the tool's brush-size to find all cells that intersect the brush
		const gridCellsThatBrushIntersects = [] as Vec2Simple[];
		const brushRadius = state.size / 2;
		for (let xOffset_world = (-brushRadius).FloorTo(guideSize); xOffset_world <= brushRadius.CeilingTo(guideSize); xOffset_world += guideSize) {
			for (let yOffset_world = (-brushRadius).FloorTo(guideSize); yOffset_world <= brushRadius.CeilingTo(guideSize); yOffset_world += guideSize) {
				const targetPoint = data.mousePos_world.Plus(new Vec3(xOffset_world, yOffset_world, 0));
				const targetPoint_diffFromObjPos = targetPoint.Minus(objPos);
				const cellRect_world = new Rect(objPos.x + targetPoint_diffFromObjPos.x.FloorTo(guideSize), objPos.y + targetPoint_diffFromObjPos.y.FloorTo(guideSize), guideSize, guideSize, true);

				const gridCellXAndY = targetPoint_diffFromObjPos.NewX(x=>(x / guideSize).FloorTo(1)).NewY(y=>(y / guideSize).FloorTo(1)).ToVec2();
				if (this.ToolState.shape == MeshModifyBrushShape.square) {
					// check if brush square intersects the grid-cell square
					const brushRect_world = new Rect(data.mousePos_world.x - brushRadius, data.mousePos_world.y - brushRadius, state.size, state.size, true);
					if (brushRect_world.Intersects(cellRect_world)) {
						gridCellsThatBrushIntersects.push([gridCellXAndY.x, gridCellXAndY.y]);
					}
				} else if (this.ToolState.shape == MeshModifyBrushShape.circle) {
					// check if brush circle intersects the grid-cell square
					const closestCellPointToTargetPoint = new Vec2(data.mousePos_world.x.KeepBetween(cellRect_world.Left, cellRect_world.Right), data.mousePos_world.y.KeepBetween(cellRect_world.Bottom, cellRect_world.Top));
					const brushCenterToClosestCellPointDist = closestCellPointToTargetPoint.DistanceTo(data.mousePos_world.ToVec2());
					if (brushCenterToClosestCellPointDist <= brushRadius) {
						gridCellsThatBrushIntersects.push([gridCellXAndY.x, gridCellXAndY.y]);
					}
				}
			}
		}

		if (objMesh.guide_shape == GuideShape.square) {
			const prevPendingUpdate = state.pendingObjMeshUpdates.get(obj.id);
			const prevObjMeshPoints = prevPendingUpdate?.newPoints ?? objMesh.points;
			let gridCells = PolygonToGridCells(prevObjMeshPoints, guideSize);

			if (state.subtool == MeshModifySubtool.grow) {
				// for each grid-cell intersecting brush, expand polygon to include that grid-cell area
				for (const gridCell of gridCellsThatBrushIntersects) {
					/*const cellRect = new Rect(gridCell.x, gridCell.y, gridCell.x + guideSize, gridCell.y + guideSize);
					console.log("Adding cellRect:", cellRect);*/
					//gridCells.push([gridCell.x, gridCell.y]); // duplicates are fine, because GeneratePolygonFromCells will remove them
					gridCells.push(gridCell); // duplicates are fine, because GeneratePolygonFromCells will remove them
				}
			}
			else {
				// for each grid-cell intersecting brush, remove it

				// approach 2
				/*const gridCellsThatBrushIntersects_set = new Set(gridCellsThatBrushIntersects.map(a=>`${a[0]},${a[1]}`));
				const gridCells_approach2 = gridCells.filter(cell=>!gridCellsThatBrushIntersects_set.has(`${cell[0]},${cell[1]}`));*/

				// approach 3; did a speed-test, and this is ~3x as fast as the simpler approach above
				const gridCellsThatBrushIntersects_mapOfSets = new Map<number, Set<number>>(); // xIndexesWithCells -> yIndexesOfCells_forGivenX
				for (const gridCell of gridCellsThatBrushIntersects) {
					if (!gridCellsThatBrushIntersects_mapOfSets.has(gridCell[0])) {
						gridCellsThatBrushIntersects_mapOfSets.set(gridCell[0], new Set<number>());
					}
					gridCellsThatBrushIntersects_mapOfSets.get(gridCell[0])!.add(gridCell[1]);
				}
				gridCells = gridCells.filter(cell=>!gridCellsThatBrushIntersects_mapOfSets.get(cell[0])?.has(cell[1]));
			}
			// convert grid-cells to polygon
			const newPolygon = GridCellsToPolygon(gridCells, guideSize);

			if (newPolygon && newPolygon.length >= 4) {
				const newBounds = GetPolygonBounds(newPolygon);
				const newPolygonBigEnough = newBounds.maxX - newBounds.minX >= guideSize && newBounds.maxY - newBounds.minY >= guideSize;
				// update the polygon (if it's valid, and has changed)
				if (newPolygon != null && newPolygonBigEnough && JSON.stringify(newPolygon) != JSON.stringify(prevObjMeshPoints)) {
					RunInAction("Scene_OnPointerMove", ()=>state.pendingObjMeshUpdates.set(obj.id, new ObjMeshUpdate({meshIndex: obj.meshes.indexOf(objMesh), newPoints: newPolygon})));
				}
			}
		}
	}
	Document_OnPointerUp(data: ToolData.Document_OnPointerUp) {
		if (data.event.button != 0) return; // left mouse-button only
		if (!this.ToolState.opInProgress) return;
		//this.opInProgress = false;

		const mapID = GetOpenMapID.CatchBail(null);
		const mapState = GetMapState.CatchBail(null, mapID);
		if (mapID == null || mapState == null) return;
		const obj = GetMapObject.CatchBail(null, mapState.activeObject, mapID);
		if (obj == null) return;
		const state = this.ToolState;

		const pendingUpdate_points = state.pendingObjMeshUpdates.get(obj.id);
		if (pendingUpdate_points != null) {
			PushPendingMeshUpdateForObj(obj, pendingUpdate_points, state);
		}

		this.ToolState.EndCurrentOp(false); // false, to not clear pending-update (we wait to do that when PushPendingMeshUpdateForObj finishes sending the update)
	}
}

async function PushPendingMeshUpdateForObj(obj: MapObject, update: ObjMeshUpdate, state: ToolState_MeshModify) {
	const newMeshes = Clone(obj.meshes) as ObjMesh[];
	newMeshes[update.meshIndex].points = update.newPoints;
	update.sendStarted = true;
	await new UpdateMapObject({
		id: obj.id,
		updates: {meshes: newMeshes},
	}).RunOnServer();

	// if the update was not overridden by a new pending update, remove it from the pending list (just for data-cleanliness/accuracy purposes; not strictly necessary)
	if (state.pendingObjMeshUpdates.get(obj.id) == update) {
		RunInAction("PushPendingMeshUpdateForObj", ()=>state.pendingObjMeshUpdates.delete(obj.id));
	}
}